import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Badge, Spinner, Row, Col, Card, CardBody, CardTitle, CardText,
  Input, Label, Button, Table, Form, ButtonGroup,
} from 'reactstrap';
import { PageHeader } from '../components/PageHeader';
import { OverviewGrid } from '../components/OverviewGrid';
import { priceFetch, isPriceProxyEnabled } from '../price/priceClient';
import { ApiProxyGuard } from '../components/ApiProxyGuard';
import { ApiHealthStrip } from '../components/ApiHealthStrip';

/**
 * Price API — sammelt Preise + Verfügbarkeit (online & lokale Läden) von
 * Produkten (Scraping via Probe), mit Preishistorie und Affiliate-Tracking.
 * Verdrahtet gegen /proxy/price.
 */
// #116: delegiert an den gemeinsamen ApiProxyGuard (statt lokaler Kopie).
function ProxyGuard({ children }) {
  return (
    <ApiProxyGuard enabled={isPriceProxyEnabled()} label="Price" envVar="PRICE_API_URL" proxyPath="/proxy/price">
      {children}
    </ApiProxyGuard>
  );
}

const fmtPrice = (p, cur) => (p == null ? '–' : `${Number(p).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur || ''}`.trim());
const stockBadge = (v) => (v === true ? <Badge color="success" pill>auf Lager</Badge>
  : v === false ? <Badge color="danger" pill>ausverkauft</Badge>
  : <Badge color="secondary" pill>unbekannt</Badge>);
// Versand-Anzeige: gratis / Betrag / unbekannt.
const shipCell = (p) => (p.shipping_free ? <Badge color="success" pill>gratis</Badge>
  : p.shipping_cost != null ? fmtPrice(p.shipping_cost, p.currency)
  : <span className="text-muted" title="Versand unbekannt (z.B. erst im Checkout)">?</span>);
// Gesamtpreis (Produkt+Versand); markiert unbekannten Versand mit „+Versand".
const totalCell = (p) => (p.total_price == null ? '–'
  : <>{fmtPrice(p.total_price, p.currency)}{!p.shipping_known ? <span className="text-muted small"> +Versand?</span> : null}</>);
// Zielländer für die Versand-/Gesamtpreis-Auswahl (leer = Produkt-Default).
const COUNTRIES = ['DE', 'AT', 'CH', 'FR', 'NL', 'BE', 'IT', 'ES', 'PL', 'GB', 'US'];

const CARDS = [
  { to: '/admin/apis/price/sources', icon: '🗂️', title: 'Quellen & Schedule', status: 'live',
    description: 'Shop-Listings als Rezept (Probe) oder Connector (JSON-LD/HTTP/HTML) anlegen und planen.' },
  { to: '/admin/apis/price/test', icon: '🧪', title: 'Seite testen', status: 'live',
    description: 'Dry-Run: eine Listing-Seite scrapen und die extrahierten Produkte prüfen.' },
  { to: '/admin/apis/price/catalog', icon: '🛒', title: 'Produkt-Katalog', status: 'live',
    description: 'Produkte mit Shop, URL, Bild, Preis, Verfügbarkeit — inkl. Preishistorie.' },
  { to: '/admin/apis/price/stores', icon: '🏬', title: 'Läden', status: 'live',
    description: 'Filialen und ihre lokale Verfügbarkeit (Click & Collect / Filialbestand).' },
  { to: '/admin/apis/price/stats', icon: '📊', title: 'Statistik', status: 'live',
    description: 'Katalog-Kennzahlen: Produkte je Shop (Ø-Preis) und Kategorie, Historie & lokale Bestände.' },
];

// ── Übersicht ───────────────────────────────────────────────────────────
export function PriceOverviewPage() {
  const enabled = isPriceProxyEnabled();
  const health = useQuery({ queryKey: ['price-health'], queryFn: () => priceFetch('/healthz'), refetchInterval: 30_000, retry: false, enabled });
  const mon = useQuery({ queryKey: ['price-monitoring'], queryFn: () => priceFetch('/v1/monitoring'), refetchInterval: 20_000, retry: false, enabled });
  const m = mon.data || {};

  return (
    <div>
      <PageHeader title="Price API · Übersicht"
        subtitle="Preise & Verfügbarkeit (online + lokale Läden) sammeln, Preishistorie führen, Affiliate-Links anreichern." />
      <ProxyGuard>
        <ApiHealthStrip health={health}>
          {m.status && <Badge color={m.status === 'ok' ? 'success' : 'warning'} pill className="ms-2">{m.status}</Badge>}
        </ApiHealthStrip>
        <Row className="g-3 mb-3">
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Produkte</CardTitle>
            <CardText className="fs-4">{m.products_total ?? '–'}</CardText>
            <div className="small text-muted">{m.products_in_stock ?? 0} auf Lager</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Preis-Punkte</CardTitle>
            <CardText className="fs-4">{m.price_history_points ?? '–'}</CardText>
            <div className="small text-muted">Historie gesamt</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Läden</CardTitle>
            <CardText className="fs-4">{m.stores_total ?? '–'}</CardText>
            <div className="small text-muted">lokale Verfügbarkeit</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Quellen</CardTitle>
            <CardText className="fs-4">{m.sources_enabled ?? '–'}<span className="fs-6 text-muted">/{m.sources_total ?? '–'}</span></CardText>
            <div className="small text-muted">{m.sources_error ? <Badge color="danger" pill>{m.sources_error} Fehler</Badge> : 'aktiv'}</div>
          </CardBody></Card></Col>
        </Row>
        <OverviewGrid cards={CARDS} />
      </ProxyGuard>
    </div>
  );
}

// ── Quellen & Schedule ──────────────────────────────────────────────────
const NEW_SOURCE = {
  slug: '', name: '', shop: '', country: 'DE', kind: 'recipe', list_url: '',
  currency: 'EUR', schedule_cron: '@daily', recipe: '{}', connector: '{}',
  default_shipping_cost: '', free_shipping_threshold: '', shipping_probe: '{}',
};
export function PriceSourcesPage() {
  const qc = useQueryClient();
  const enabled = isPriceProxyEnabled();
  const sources = useQuery({ queryKey: ['price-sources'], queryFn: () => priceFetch('/v1/sources'), refetchInterval: 20_000, retry: false, enabled });
  const [form, setForm] = useState(NEW_SOURCE);
  const items = sources.data?.items || [];

  const save = useMutation({
    mutationFn: () => {
      const body = {
        slug: form.slug, name: form.name, shop: form.shop, country: form.country,
        kind: form.kind, currency: form.currency || undefined,
        schedule_cron: form.schedule_cron || undefined,
        default_shipping_cost: form.default_shipping_cost !== '' ? Number(form.default_shipping_cost) : undefined,
        free_shipping_threshold: form.free_shipping_threshold !== '' ? Number(form.free_shipping_threshold) : undefined,
      };
      if (form.list_url) body.list_url = form.list_url;
      try { body.recipe = JSON.parse(form.recipe || '{}'); } catch { throw new Error('recipe ist kein gültiges JSON'); }
      try { body.connector = JSON.parse(form.connector || '{}'); } catch { throw new Error('connector ist kein gültiges JSON'); }
      try { body.shipping_probe = JSON.parse(form.shipping_probe || '{}'); } catch { throw new Error('shipping_probe ist kein gültiges JSON'); }
      return priceFetch('/v1/sources', { method: 'POST', body });
    },
    onSuccess: () => { setForm(NEW_SOURCE); qc.invalidateQueries({ queryKey: ['price-sources'] }); },
  });
  const scrape = useMutation({
    mutationFn: (slug) => priceFetch(`/v1/sources/${encodeURIComponent(slug)}/scrape`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['price-sources'] }),
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <PageHeader title="Price API · Quellen & Schedule" subtitle="Shop-Listings als Rezept (Probe) oder Connector anlegen und automatisch scrapen." />
      <ProxyGuard>
        <Card className="mb-3"><CardBody>
          <CardTitle tag="h6">Neue Quelle</CardTitle>
          <Form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
            <Row className="g-2">
              <Col md={3}><Label className="small">Slug</Label><Input bsSize="sm" value={form.slug} onChange={set('slug')} placeholder="mediamarkt-gpus" /></Col>
              <Col md={3}><Label className="small">Name</Label><Input bsSize="sm" value={form.name} onChange={set('name')} placeholder="MediaMarkt Grafikkarten" /></Col>
              <Col md={3}><Label className="small">Shop</Label><Input bsSize="sm" value={form.shop} onChange={set('shop')} placeholder="MediaMarkt" /></Col>
              <Col md={1}><Label className="small">Land</Label><Input bsSize="sm" value={form.country} onChange={set('country')} /></Col>
              <Col md={2}><Label className="small">Währung</Label><Input bsSize="sm" value={form.currency} onChange={set('currency')} /></Col>
              <Col md={3}><Label className="small">Art</Label>
                <Input type="select" bsSize="sm" value={form.kind} onChange={set('kind')}>
                  {['recipe', 'jsonld', 'http_json', 'html_css', 'sitemap'].map((k) => <option key={k}>{k}</option>)}
                </Input>
              </Col>
              <Col md={4}><Label className="small">List-URL</Label><Input bsSize="sm" value={form.list_url} onChange={set('list_url')} placeholder="https://…" /></Col>
              <Col md={2}><Label className="small">Cron</Label><Input bsSize="sm" value={form.schedule_cron} onChange={set('schedule_cron')} placeholder="@daily" /></Col>
              <Col md={3}><Label className="small">Versand-Pauschale</Label><Input bsSize="sm" type="number" step="0.01" value={form.default_shipping_cost} onChange={set('default_shipping_cost')} placeholder="z.B. 4.95 (falls nicht scrapebar)" /></Col>
              <Col md={3}><Label className="small">Gratis-Versand ab</Label><Input bsSize="sm" type="number" step="0.01" value={form.free_shipping_threshold} onChange={set('free_shipping_threshold')} placeholder="z.B. 50" /></Col>
              <Col md={6}><Label className="small">recipe (JSON)</Label><Input type="textarea" bsSize="sm" rows={4} style={{ fontFamily: 'monospace' }} value={form.recipe} onChange={set('recipe')} /></Col>
              <Col md={6}><Label className="small">connector (JSON)</Label><Input type="textarea" bsSize="sm" rows={4} style={{ fontFamily: 'monospace' }} value={form.connector} onChange={set('connector')} /></Col>
              <Col md={12}><Label className="small">shipping_probe (JSON) — Versand-Test per Warenkorb, z.B. <code>{'{ "strategy": "shopify", "countries": ["DE","AT","CH"] }'}</code></Label>
                <Input type="textarea" bsSize="sm" rows={3} style={{ fontFamily: 'monospace' }} value={form.shipping_probe} onChange={set('shipping_probe')} /></Col>
            </Row>
            <Button size="sm" color="primary" className="mt-2" disabled={save.isPending} type="submit">{save.isPending ? <Spinner size="sm" /> : 'Quelle speichern'}</Button>
            {save.isError && <Alert color="danger" className="small mt-2">{save.error?.message}</Alert>}
          </Form>
        </CardBody></Card>

        <Card><CardBody>
          <CardTitle tag="h6">Quellen <Badge color="secondary" pill>{items.length}</Badge></CardTitle>
          {sources.isLoading ? <Spinner size="sm" /> : !items.length ? <CardText className="small text-muted">Noch keine Quellen.</CardText> : (
            <Table size="sm" responsive hover className="small">
              <thead><tr><th>Slug</th><th>Shop</th><th>Art</th><th>Letzter Lauf</th><th>Status</th><th>#</th><th></th></tr></thead>
              <tbody>{items.map((s) => (
                <tr key={s.id}>
                  <td>{s.slug}<div className="text-muted">{s.name}</div></td>
                  <td>{s.shop}</td>
                  <td><Badge color="light" className="text-dark border">{s.kind}</Badge></td>
                  <td className="text-muted">{s.last_run_at ? new Date(s.last_run_at).toLocaleString() : '—'}</td>
                  <td>{s.last_status ? <Badge color={s.last_status === 'failed' ? 'danger' : s.last_status === 'completed' ? 'success' : 'info'} pill>{s.last_status}</Badge> : '—'}</td>
                  <td>{s.last_product_count ?? '—'}</td>
                  <td><Button size="sm" color="link" className="p-0" disabled={scrape.isPending} onClick={() => scrape.mutate(s.slug)}>scrapen</Button></td>
                </tr>
              ))}</tbody>
            </Table>
          )}
          {scrape.isError && <Alert color="danger" className="small mt-2">{scrape.error?.message}</Alert>}
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}

// ── Seite testen (Dry-Run) ──────────────────────────────────────────────
export function PriceTestPage() {
  const [form, setForm] = useState({ kind: 'jsonld', list_url: '', shop: 'dry-run', currency: 'EUR', recipe: '{}', connector: '{\n  "url": ""\n}' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const test = useMutation({
    mutationFn: () => {
      const body = { kind: form.kind, shop: form.shop, currency: form.currency || undefined };
      if (form.list_url) body.list_url = form.list_url;
      try { body.recipe = JSON.parse(form.recipe || '{}'); } catch { throw new Error('recipe ist kein gültiges JSON'); }
      try { body.connector = JSON.parse(form.connector || '{}'); } catch { throw new Error('connector ist kein gültiges JSON'); }
      return priceFetch('/v1/sources/test', { method: 'POST', body });
    },
  });
  const r = test.data;

  return (
    <div>
      <PageHeader title="Price API · Seite testen" subtitle="Dry-Run: scrapt eine Seite synchron und zeigt die extrahierten Produkte (nicht gespeichert)." />
      <ProxyGuard>
        <Card className="mb-3"><CardBody>
          <Form onSubmit={(e) => { e.preventDefault(); test.mutate(); }}>
            <Row className="g-2 align-items-end">
              <Col md={3}><Label className="small">Art</Label>
                <Input type="select" bsSize="sm" value={form.kind} onChange={set('kind')}>
                  {['recipe', 'jsonld', 'http_json', 'html_css', 'sitemap'].map((k) => <option key={k}>{k}</option>)}
                </Input></Col>
              <Col md={6}><Label className="small">List-URL (für recipe)</Label><Input bsSize="sm" value={form.list_url} onChange={set('list_url')} placeholder="https://…" /></Col>
              <Col md={3}><Label className="small">Währung</Label><Input bsSize="sm" value={form.currency} onChange={set('currency')} /></Col>
              <Col md={6}><Label className="small">recipe (JSON)</Label><Input type="textarea" bsSize="sm" rows={5} style={{ fontFamily: 'monospace' }} value={form.recipe} onChange={set('recipe')} /></Col>
              <Col md={6}><Label className="small">connector (JSON)</Label><Input type="textarea" bsSize="sm" rows={5} style={{ fontFamily: 'monospace' }} value={form.connector} onChange={set('connector')} /></Col>
            </Row>
            <Button size="sm" color="primary" className="mt-2" disabled={test.isPending} type="submit">{test.isPending ? <><Spinner size="sm" /> scrape …</> : 'Testen'}</Button>
            {test.isError && <Alert color="danger" className="small mt-2">{test.error?.message}</Alert>}
          </Form>
        </CardBody></Card>
        {r && (
          <Card><CardBody>
            <CardTitle tag="h6">Ergebnis <Badge color={r.status === 'completed' ? 'success' : 'warning'} pill>{r.status}</Badge>
              <span className="text-muted small ms-2">{r.extracted} extrahiert</span></CardTitle>
            {!r.sample?.length ? <CardText className="small text-muted">Keine Produkte extrahiert.</CardText> : (
              <Table size="sm" responsive hover className="small">
                <thead><tr><th>Titel</th><th>Preis</th><th>Verfügbar</th><th>URL</th></tr></thead>
                <tbody>{r.sample.map((it, k) => (
                  <tr key={k}>
                    <td>{it.title || it.name}</td>
                    <td>{it.price_text || it.price || '—'}</td>
                    <td className="small">{it.availability || it.availability_text || '—'}</td>
                    <td className="text-truncate" style={{ maxWidth: 240 }}>{it.url ? <a href={it.url} target="_blank" rel="noreferrer">{it.url}</a> : '—'}</td>
                  </tr>
                ))}</tbody>
              </Table>
            )}
          </CardBody></Card>
        )}
      </ProxyGuard>
    </div>
  );
}

// ── Produkt-Katalog (+ Preishistorie) ───────────────────────────────────
export function PriceCatalogPage() {
  const enabled = isPriceProxyEnabled();
  const [filters, setFilters] = useState({ q: '', shop: '', in_stock: '', max_price: '', free_shipping: '', country: '', scope: '', city: '', sort: 'updated' });
  const [affiliate, setAffiliate] = useState('');
  const [selected, setSelected] = useState(null);   // Produkt-ID für Historie
  const [offersFor, setOffersFor] = useState(null); // Produkt-ID für Cross-Shop-Angebote
  const [detailFor, setDetailFor] = useState(null); // Produkt-ID für Detail-/Daten-Prüfung
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const qs = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
  if (affiliate) qs.set('affiliate_id', affiliate);
  qs.set('limit', '100');

  const products = useQuery({
    queryKey: ['price-products', qs.toString()],
    queryFn: () => priceFetch(`/v1/products?${qs.toString()}`),
    retry: false, enabled,
  });
  const history = useQuery({
    queryKey: ['price-history', selected],
    queryFn: () => priceFetch(`/v1/products/${selected}/history?limit=200`),
    retry: false, enabled: enabled && selected != null,
  });
  // Länder-/Affiliate-Query-String für Detail-/Angebote-Abrufe.
  const sideQs = () => {
    const p = new URLSearchParams();
    if (filters.country) p.set('country', filters.country);
    if (affiliate) p.set('affiliate_id', affiliate);
    const s = p.toString();
    return s ? `?${s}` : '';
  };
  const offers = useQuery({
    queryKey: ['price-offers', offersFor, affiliate, filters.country],
    queryFn: () => priceFetch(`/v1/products/${offersFor}/offers${sideQs()}`),
    retry: false, enabled: enabled && offersFor != null,
  });
  const detail = useQuery({
    queryKey: ['price-detail', detailFor, filters.country],
    queryFn: () => priceFetch(`/v1/products/${detailFor}${sideQs()}`),
    retry: false, enabled: enabled && detailFor != null,
  });
  const items = products.data?.items || [];
  const d = detail.data;
  const h = history.data;
  const maxP = Math.max(1, ...(h?.points || []).map((p) => Number(p.price) || 0));

  return (
    <div>
      <PageHeader title="Price API · Produkt-Katalog" subtitle="Produktliste zur Datenprüfung: Shop, URL, Bild, Preis, EAN & Verfügbarkeit. Zielland wählen → Versand/Gesamt je Land (Warenkorb-Probe). „Details“ zeigt alle Felder + Versand je Land + rohe Scrape-Zeile, „Angebote“ den Cross-Shop-Vergleich, „Historie“ den Preisverlauf." />
      <ProxyGuard>
        <Card className="mb-3"><CardBody>
          <Row className="g-2 align-items-end">
            <Col md={3}><Label className="small">Suche</Label><Input bsSize="sm" value={filters.q} onChange={set('q')} placeholder="Titel …" /></Col>
            <Col md={2}><Label className="small">Shop</Label><Input bsSize="sm" value={filters.shop} onChange={set('shop')} /></Col>
            <Col md={2}><Label className="small">Verfügbar</Label>
              <Input type="select" bsSize="sm" value={filters.in_stock} onChange={set('in_stock')}>
                <option value="">alle</option><option value="true">auf Lager</option><option value="false">nicht</option>
              </Input></Col>
            <Col md={2}><Label className="small">Max-Preis</Label><Input bsSize="sm" type="number" value={filters.max_price} onChange={set('max_price')} /></Col>
            <Col md={2}><Label className="small">Verfügbarkeit</Label>
              <Input type="select" bsSize="sm" value={filters.scope} onChange={set('scope')}>
                <option value="">alle (online + lokal)</option>
                <option value="online">nur online</option>
                <option value="local">nur lokal (Filiale)</option>
              </Input></Col>
            <Col md={2}><Label className="small">Stadt (Filiale)</Label><Input bsSize="sm" value={filters.city} onChange={set('city')} placeholder="z.B. Aachen" /></Col>
            <Col md={2}><Label className="small">Zielland (Versand)</Label>
              <Input type="select" bsSize="sm" value={filters.country} onChange={set('country')}>
                <option value="">Produkt-Default</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Input></Col>
            <Col md={2}><Label className="small">Versand</Label>
              <Input type="select" bsSize="sm" value={filters.free_shipping} onChange={set('free_shipping')}>
                <option value="">alle</option><option value="true">nur gratis</option>
              </Input></Col>
            <Col md={3}><Label className="small">Sortierung</Label>
              <Input type="select" bsSize="sm" value={filters.sort} onChange={set('sort')}>
                <option value="updated">zuletzt aktualisiert</option>
                <option value="price_asc">Preis ↑</option><option value="price_desc">Preis ↓</option>
                <option value="total_asc">Gesamtpreis ↑</option><option value="total_desc">Gesamtpreis ↓</option>
              </Input></Col>
            <Col md={4}><Label className="small">Affiliate-ID (Tracking)</Label><Input bsSize="sm" value={affiliate} onChange={(e) => setAffiliate(e.target.value)} placeholder="z.B. valitype-21" /></Col>
          </Row>
        </CardBody></Card>

        <Card><CardBody>
          <CardTitle tag="h6">Produkte <Badge color="secondary" pill>{items.length}</Badge></CardTitle>
          {products.isLoading ? <Spinner size="sm" /> : products.isError ? <Alert color="danger" className="small">{products.error?.message}</Alert>
            : !items.length ? <CardText className="small text-muted">Keine Produkte.</CardText> : (
              <Table size="sm" responsive hover className="small align-middle">
                <thead><tr><th></th><th>Produkt</th><th>EAN</th><th>Shop</th><th>Preis</th><th>Versand</th><th>Gesamt</th><th>Online</th><th>Lokal</th><th>Link</th><th></th></tr></thead>
                <tbody>{items.map((p) => (
                  <tr key={p.id}>
                    <td>{p.image_url ? <img src={p.image_url} alt="" style={{ width: 36, height: 36, objectFit: 'contain' }} /> : null}</td>
                    <td>{p.title}<div className="text-muted">{p.brand || ''}</div></td>
                    <td>{p.gtin ? <code className="small">{p.gtin}</code> : <span className="text-muted small" title={p.match_key || ''}>Name-Match</span>}</td>
                    <td>{p.shop}</td>
                    <td>{fmtPrice(p.price, p.currency)}</td>
                    <td>{shipCell(p)}</td>
                    <td className="fw-bold">{totalCell(p)}</td>
                    <td>{stockBadge(p.in_stock)}</td>
                    <td>{Number(p.local_stores) > 0
                      ? <><Badge color="success" pill>{p.local_stores} Filiale{Number(p.local_stores) > 1 ? 'n' : ''}</Badge>{p.local_min_price != null ? <div className="small text-muted">ab {fmtPrice(p.local_min_price, p.currency)}</div> : null}</>
                      : <span className="text-muted small">—</span>}</td>
                    <td>{(p.affiliate_url || p.url) ? <a href={p.affiliate_url || p.url} target="_blank" rel="noreferrer">{p.affiliate_url ? 'Affiliate ↗' : 'Shop ↗'}</a> : '—'}</td>
                    <td className="text-nowrap">
                      <Button size="sm" color="link" className="p-0" onClick={() => setDetailFor(p.id)}>Details</Button>
                      <span className="text-muted mx-1">·</span>
                      <Button size="sm" color="link" className="p-0" onClick={() => setOffersFor(p.id)}>Angebote</Button>
                      <span className="text-muted mx-1">·</span>
                      <Button size="sm" color="link" className="p-0" onClick={() => setSelected(p.id)}>Historie</Button>
                    </td>
                  </tr>
                ))}</tbody>
              </Table>
            )}
        </CardBody></Card>

        {detailFor != null && (
          <Card className="mt-3"><CardBody>
            <CardTitle tag="h6">Details · Produkt #{detailFor}
              <Button size="sm" color="link" className="p-0 ms-2" onClick={() => setDetailFor(null)}>schließen</Button></CardTitle>
            {detail.isLoading ? <Spinner size="sm" /> : detail.isError ? <Alert color="danger" className="small">{detail.error?.message}</Alert> : d && (
              <Row className="g-3">
                <Col md={3}>
                  {d.image_url
                    ? <img src={d.image_url} alt="" style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain' }} />
                    : <div className="text-muted small">kein Bild</div>}
                  <div className="small text-muted mt-1 text-break">{d.image_url}</div>
                </Col>
                <Col md={9}>
                  <Table size="sm" borderless className="small mb-2"><tbody>
                    <tr><td className="text-muted" style={{ width: 160 }}>Titel</td><td>{d.title}</td></tr>
                    <tr><td className="text-muted">Marke</td><td>{d.brand || '—'}</td></tr>
                    <tr><td className="text-muted">Shop</td><td>{d.shop} <span className="text-muted">(Quelle: {d.source})</span></td></tr>
                    <tr><td className="text-muted">EAN / GTIN</td><td>{d.gtin ? <code>{d.gtin}</code> : <span className="text-muted">— (Name-Match)</span>}</td></tr>
                    <tr><td className="text-muted">MPN / SKU</td><td>{[d.mpn, d.sku].filter(Boolean).join(' · ') || '—'}</td></tr>
                    <tr><td className="text-muted">Kategorie</td><td>{d.category || '—'}</td></tr>
                    <tr><td className="text-muted">Preis</td><td>{fmtPrice(d.price, d.currency)} {d.price_text ? <span className="text-muted">(roh: „{d.price_text}“)</span> : null}</td></tr>
                    <tr><td className="text-muted">Versand</td><td>{shipCell(d)} {d.shipping_text ? <span className="text-muted">„{d.shipping_text}“</span> : (!d.shipping_known ? <span className="text-muted">unbekannt (erst im Checkout)</span> : null)}</td></tr>
                    <tr><td className="text-muted">Gesamtpreis</td><td className="fw-bold">{totalCell(d)}</td></tr>
                    <tr><td className="text-muted">Verfügbar</td><td>{stockBadge(d.in_stock)} {d.availability_text ? <span className="text-muted">„{d.availability_text}“</span> : null}</td></tr>
                    <tr><td className="text-muted">Produkt-URL</td><td>{d.url ? <a href={d.url} target="_blank" rel="noreferrer" className="text-break">{d.url}</a> : '—'}</td></tr>
                    <tr><td className="text-muted">match_key</td><td><code className="small">{d.match_key || '—'}</code></td></tr>
                    <tr><td className="text-muted">name_key</td><td><code className="small">{d.name_key || '—'}</code></td></tr>
                    <tr><td className="text-muted">gesehen</td><td className="text-muted">zuerst {d.first_seen_at ? new Date(d.first_seen_at).toLocaleString() : '—'} · zuletzt {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : '—'} · Scrape {d.scraped_at ? new Date(d.scraped_at).toLocaleString() : '—'}</td></tr>
                  </tbody></Table>

                  {Array.isArray(d.shipping_by_country) && d.shipping_by_country.length > 0 && (
                    <div className="mb-2">
                      <div className="small fw-bold">Versand je Land (Warenkorb-Probe, {d.shipping_by_country.length})</div>
                      <Table size="sm" responsive className="small mb-0">
                        <thead><tr><th>Land</th><th>Versand</th><th>Gesamt</th><th>Carrier</th><th>geprüft</th></tr></thead>
                        <tbody>{d.shipping_by_country.map((r) => (
                          <tr key={r.country}>
                            <td><Badge color="light" className="text-dark border">{r.country}</Badge></td>
                            <td>{r.deliverable === false ? <span className="text-danger">nicht lieferbar</span> : (r.free ? <Badge color="success" pill>gratis</Badge> : fmtPrice(r.cost, r.currency))}</td>
                            <td className="fw-bold">{r.deliverable === false ? '—' : fmtPrice(d.price != null && r.cost != null ? Number(d.price) + (r.free ? 0 : Number(r.cost)) : (r.free ? d.price : null), r.currency || d.currency)}</td>
                            <td className="text-muted">{r.method || '—'}</td>
                            <td className="text-muted">{r.observed_at ? new Date(r.observed_at).toLocaleDateString() : '—'}</td>
                          </tr>
                        ))}</tbody>
                      </Table>
                    </div>
                  )}

                  {Array.isArray(d.stores) && d.stores.length > 0 && (
                    <div className="mb-2">
                      <div className="small fw-bold">Lokale Verfügbarkeit ({d.stores.length})</div>
                      <Table size="sm" responsive className="small mb-0">
                        <thead><tr><th>Laden</th><th>Stadt</th><th>Bestand</th><th>Preis</th></tr></thead>
                        <tbody>{d.stores.map((st) => (
                          <tr key={st.slug}><td>{st.name}</td><td>{st.city || '—'}</td>
                            <td>{stockBadge(st.in_stock)}{st.stock_count != null ? ` (${st.stock_count})` : ''}</td>
                            <td>{fmtPrice(st.price, st.currency)}</td></tr>
                        ))}</tbody>
                      </Table>
                    </div>
                  )}

                  <details>
                    <summary className="small text-muted" style={{ cursor: 'pointer' }}>Rohe Scrape-Zeile (raw)</summary>
                    <pre className="small mt-1 p-2 bg-body-secondary rounded" style={{ maxHeight: 240, overflow: 'auto' }}>{JSON.stringify(d.raw ?? {}, null, 2)}</pre>
                  </details>
                </Col>
              </Row>
            )}
          </CardBody></Card>
        )}

        {offersFor != null && (
          <Card className="mt-3"><CardBody>
            <CardTitle tag="h6">Angebote (Cross-Shop) · Produkt #{offersFor}
              <Button size="sm" color="link" className="p-0 ms-2" onClick={() => setOffersFor(null)}>schließen</Button></CardTitle>
            {offers.isLoading ? <Spinner size="sm" /> : offers.isError ? <Alert color="danger" className="small">{offers.error?.message}</Alert> : offers.data && (
              <>
                <div className="small text-muted mb-2">
                  gematcht über <Badge color={offers.data.matched_by === 'ean' ? 'success' : 'info'} pill>{offers.data.matched_by === 'ean' ? `EAN ${offers.data.gtin}` : offers.data.matched_by === 'name' ? 'Name' : 'ID'}</Badge>
                  {' '}· {offers.data.shops} Shop(s) · günstigster Gesamtpreis <strong>{fmtPrice(offers.data.best_total ?? offers.data.best_price, offers.data.items?.[0]?.currency)}</strong>{offers.data.best_shop ? ` (${offers.data.best_shop})` : ''}
                </div>
                {!offers.data.items?.length ? <CardText className="small text-muted">Keine Angebote.</CardText> : (
                  <Table size="sm" responsive hover className="small align-middle">
                    <thead><tr><th>Shop</th><th>Produkt</th><th>Preis</th><th>Versand</th><th>Gesamt</th><th>Verfügbar</th><th>Link</th></tr></thead>
                    <tbody>{offers.data.items.map((o, k) => (
                      <tr key={o.id} className={k === 0 ? 'table-success' : ''}>
                        <td>{o.shop}</td>
                        <td>{o.title}</td>
                        <td>{fmtPrice(o.price, o.currency)}</td>
                        <td>{shipCell(o)}</td>
                        <td className="fw-bold">{totalCell(o)}</td>
                        <td>{stockBadge(o.in_stock)}</td>
                        <td>{(o.affiliate_url || o.url) ? <a href={o.affiliate_url || o.url} target="_blank" rel="noreferrer">{o.affiliate_url ? 'Affiliate ↗' : 'Shop ↗'}</a> : '—'}</td>
                      </tr>
                    ))}</tbody>
                  </Table>
                )}
              </>
            )}
          </CardBody></Card>
        )}

        {selected != null && (
          <Card className="mt-3"><CardBody>
            <CardTitle tag="h6">Preishistorie · Produkt #{selected}
              <Button size="sm" color="link" className="p-0 ms-2" onClick={() => setSelected(null)}>schließen</Button></CardTitle>
            {history.isLoading ? <Spinner size="sm" /> : history.isError ? <Alert color="danger" className="small">{history.error?.message}</Alert> : h && (
              <>
                <div className="small text-muted mb-2">
                  min {fmtPrice(h.min_price, h.current?.currency)} · max {fmtPrice(h.max_price, h.current?.currency)} · ø {fmtPrice(h.avg_price, h.current?.currency)} · {h.count} Punkte
                </div>
                {!h.points?.length ? <CardText className="small text-muted">Keine Historie.</CardText> : (
                  <>
                    <div className="d-flex align-items-end gap-1" style={{ height: 80 }}>
                      {[...h.points].reverse().map((p, k) => (
                        <div key={k} title={`${new Date(p.observed_at).toLocaleString()}: ${fmtPrice(p.price, p.currency)}`}
                          className="flex-fill bg-primary rounded-top" style={{ height: `${Math.round(((Number(p.price) || 0) / maxP) * 100)}%`, minHeight: 2 }} />
                      ))}
                    </div>
                    <Table size="sm" responsive className="small mt-2">
                      <thead><tr><th>Zeitpunkt</th><th>Preis</th><th>Verfügbar</th></tr></thead>
                      <tbody>{h.points.slice(0, 20).map((p, k) => (
                        <tr key={k}><td className="text-muted">{new Date(p.observed_at).toLocaleString()}</td><td>{fmtPrice(p.price, p.currency)}</td><td>{stockBadge(p.in_stock)}</td></tr>
                      ))}</tbody>
                    </Table>
                  </>
                )}
              </>
            )}
          </CardBody></Card>
        )}
      </ProxyGuard>
    </div>
  );
}

// ── Läden (lokale Verfügbarkeit) ────────────────────────────────────────
export function PriceStoresPage() {
  const enabled = isPriceProxyEnabled();
  const [q, setQ] = useState({ q: '', city: '', chain: '' });
  const set = (k) => (e) => setQ((f) => ({ ...f, [k]: e.target.value }));
  const qs = new URLSearchParams();
  Object.entries(q).forEach(([k, v]) => { if (v) qs.set(k, v); });

  const stores = useQuery({
    queryKey: ['price-stores', qs.toString()],
    queryFn: () => priceFetch(`/v1/stores?${qs.toString()}`),
    retry: false, enabled,
  });
  const items = stores.data?.items || [];

  return (
    <div>
      <PageHeader title="Price API · Läden" subtitle="Filialen und ihre lokale Produkt-Verfügbarkeit (Click & Collect / Filialbestand)." />
      <ProxyGuard>
        <Card className="mb-3"><CardBody>
          <Row className="g-2 align-items-end">
            <Col md={4}><Label className="small">Suche (Name)</Label><Input bsSize="sm" value={q.q} onChange={set('q')} /></Col>
            <Col md={3}><Label className="small">Stadt</Label><Input bsSize="sm" value={q.city} onChange={set('city')} /></Col>
            <Col md={3}><Label className="small">Kette</Label><Input bsSize="sm" value={q.chain} onChange={set('chain')} /></Col>
          </Row>
        </CardBody></Card>
        <Card><CardBody>
          <CardTitle tag="h6">Läden <Badge color="secondary" pill>{items.length}</Badge></CardTitle>
          {stores.isLoading ? <Spinner size="sm" /> : !items.length ? <CardText className="small text-muted">Keine Läden.</CardText> : (
            <Table size="sm" responsive hover className="small">
              <thead><tr><th>Laden</th><th>Kette</th><th>Stadt</th><th>Produkte</th><th>auf Lager</th></tr></thead>
              <tbody>{items.map((s) => (
                <tr key={s.slug}>
                  <td>{s.name}<div className="text-muted">{s.address || ''}</div></td>
                  <td>{s.chain || '—'}</td>
                  <td>{s.city || '—'}</td>
                  <td>{s.product_count}</td>
                  <td><Badge color="success" pill>{s.in_stock_count}</Badge></td>
                </tr>
              ))}</tbody>
            </Table>
          )}
        </CardBody></Card>
      </ProxyGuard>
    </div>
  );
}

// ── Statistik ───────────────────────────────────────────────────────────
export function PriceStatsPage() {
  const enabled = isPriceProxyEnabled();
  const stats = useQuery({ queryKey: ['price-stats'], queryFn: () => priceFetch('/v1/products/stats'), refetchInterval: 30_000, retry: false, enabled });
  const s = stats.data || {};
  const byShop = s.byShop || [];
  const byCategory = s.byCategory || [];
  const maxShop = Math.max(1, ...byShop.map((r) => Number(r.n) || 0));
  const maxCat = Math.max(1, ...byCategory.map((r) => Number(r.n) || 0));

  return (
    <div>
      <PageHeader title="Price API · Statistik"
        subtitle="Katalog-Kennzahlen: Produkte je Shop/Kategorie, Preishistorie und lokale Verfügbarkeit." />
      <ProxyGuard>
        <Row className="g-3 mb-3">
          <Col md={2}><Card><CardBody><CardTitle tag="h6">Produkte</CardTitle><CardText className="fs-4">{s.total ?? '–'}</CardText></CardBody></Card></Col>
          <Col md={2}><Card><CardBody><CardTitle tag="h6">Auf Lager</CardTitle><CardText className="fs-4">{s.in_stock ?? '–'}</CardText></CardBody></Card></Col>
          <Col md={2}><Card><CardBody><CardTitle tag="h6">Mit Preis</CardTitle><CardText className="fs-4">{s.with_price ?? '–'}</CardText></CardBody></Card></Col>
          <Col md={2}><Card><CardBody><CardTitle tag="h6">Preis-Punkte</CardTitle><CardText className="fs-4">{s.history_points ?? '–'}</CardText></CardBody></Card></Col>
          <Col md={2}><Card><CardBody><CardTitle tag="h6">Läden</CardTitle><CardText className="fs-4">{s.stores ?? '–'}</CardText></CardBody></Card></Col>
          <Col md={2}><Card><CardBody><CardTitle tag="h6">Lokale Bestände</CardTitle><CardText className="fs-4">{s.local_availability ?? '–'}</CardText></CardBody></Card></Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col md={3}><Card><CardBody><CardTitle tag="h6">EAN-Abdeckung</CardTitle>
            <CardText className="fs-4">{s.with_ean ?? '–'}<span className="fs-6 text-muted">/{s.total ?? '–'}</span></CardText>
            <div className="small text-muted">Produkte mit zugeordneter EAN/GTIN</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Gematchte Produkte</CardTitle>
            <CardText className="fs-4">{s.matched_groups ?? '–'}</CardText>
            <div className="small text-muted">eindeutige Produkte (EAN- + Namens-Match)</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Multi-Shop-Produkte</CardTitle>
            <CardText className="fs-4">{s.multi_shop_products ?? '–'}</CardText>
            <div className="small text-muted">dasselbe Produkt in ≥2 Shops (Preisvergleich)</div>
          </CardBody></Card></Col>
          <Col md={3}><Card><CardBody><CardTitle tag="h6">Versand erfasst</CardTitle>
            <CardText className="fs-4">{s.with_shipping ?? '–'}<span className="fs-6 text-muted">/{s.total ?? '–'}</span></CardText>
            <div className="small text-muted">{s.free_shipping ?? 0} gratis · {s.shipping_probed ?? 0} per Warenkorb geprüft</div>
          </CardBody></Card></Col>
        </Row>

        <Row className="g-3">
          <Col md={6}><Card className="h-100"><CardBody>
            <CardTitle tag="h6">Produkte je Shop <Badge color="secondary" pill>{byShop.length}</Badge></CardTitle>
            {stats.isLoading ? <Spinner size="sm" /> : !byShop.length ? <CardText className="small text-muted">Keine Daten.</CardText> : (
              <Table size="sm" responsive hover className="small mb-0">
                <thead><tr><th>Shop</th><th className="text-end">Produkte</th><th className="text-end">Ø-Preis</th><th style={{ width: '30%' }}></th></tr></thead>
                <tbody>{byShop.map((r) => (
                  <tr key={r.shop}>
                    <td>{r.shop}</td>
                    <td className="text-end">{r.n}</td>
                    <td className="text-end">{r.avg_price != null ? fmtPrice(r.avg_price, '') : '–'}</td>
                    <td><div className="bg-primary rounded" style={{ height: 8, width: `${Math.round((Number(r.n) / maxShop) * 100)}%`, minWidth: 2 }} /></td>
                  </tr>
                ))}</tbody>
              </Table>
            )}
          </CardBody></Card></Col>

          <Col md={6}><Card className="h-100"><CardBody>
            <CardTitle tag="h6">Produkte je Kategorie <Badge color="secondary" pill>{byCategory.length}</Badge></CardTitle>
            {stats.isLoading ? <Spinner size="sm" /> : !byCategory.length ? <CardText className="small text-muted">Keine Kategorien erfasst.</CardText> : (
              <Table size="sm" responsive hover className="small mb-0">
                <thead><tr><th>Kategorie</th><th className="text-end">Produkte</th><th style={{ width: '40%' }}></th></tr></thead>
                <tbody>{byCategory.map((r) => (
                  <tr key={r.category}>
                    <td>{r.category}</td>
                    <td className="text-end">{r.n}</td>
                    <td><div className="bg-info rounded" style={{ height: 8, width: `${Math.round((Number(r.n) / maxCat) * 100)}%`, minWidth: 2 }} /></td>
                  </tr>
                ))}</tbody>
              </Table>
            )}
          </CardBody></Card></Col>
        </Row>
      </ProxyGuard>
    </div>
  );
}
